import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, MapPin, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

const pinIcon = L.divIcon({
  className: "",
  html: '<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#D3A13B;border:3px solid #11382A;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
  popupAnchor: [0, -28],
});

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 12, { duration: 1.2 });
  }, [target, map]);
  return null;
}

export const Distributors = () => {
  const [distributors, setDistributors] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/distributors").then((res) => setDistributors(res.data)).catch(() => {});
  }, []);

  const filtered = distributors.filter((d) =>
    `${d.name} ${d.city} ${d.type}`.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <section id="distributors" className="py-24 md:py-32 bg-muted/40" data-testid="distributors-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-2xl"
        >
          <span className="font-monotech text-xs tracking-[0.25em] uppercase text-secondary-foreground/70">
            Find Us Near You
          </span>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-primary mt-4">
            Distributor locator
          </h2>
          <p className="text-text-muted text-base md:text-lg leading-relaxed mt-5">
            Healseeal is stocked by verified pharmacies, hospital suppliers and distributors across India.
            Search your city or explore the map.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2 flex flex-col"
          >
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by city or name…"
                data-testid="distributor-search-input"
                className="pl-11 h-12 rounded-full bg-white border-border focus-visible:ring-primary"
              />
            </div>
            <div
              className="space-y-3 overflow-y-auto pr-1 max-h-[420px] lg:max-h-[480px]"
              data-lenis-prevent
              data-testid="distributor-list"
            >
              {filtered.length === 0 && (
                <p className="text-sm text-text-muted p-4" data-testid="distributor-no-results">
                  No distributors match your search.
                </p>
              )}
              {filtered.map((d, i) => (
                <button
                  key={d.id}
                  onClick={() => setSelected(d)}
                  data-testid={`distributor-item-${i}`}
                  className={`w-full text-left border rounded-2xl p-5 bg-white transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                    selected?.id === d.id ? "border-secondary shadow-md" : "border-border"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-bold text-primary">{d.name}</div>
                      <div className="text-xs text-text-muted mt-1 flex items-start gap-1.5">
                        <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-secondary-foreground/60" />
                        {d.address}
                      </div>
                      <div className="text-xs text-text-muted mt-1.5 flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-secondary-foreground/60" /> {d.phone}
                      </div>
                    </div>
                    <span className="font-monotech text-[10px] tracking-wider uppercase bg-muted text-primary px-2.5 py-1 rounded-full shrink-0">
                      {d.city}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-3 rounded-3xl overflow-hidden border border-border h-[420px] lg:h-[540px] relative z-0"
            data-lenis-prevent
            data-testid="distributor-map"
          >
            <MapContainer
              center={[21.5, 79]}
              zoom={5}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FlyTo target={selected} />
              {distributors.map((d) => (
                <Marker key={d.id} position={[d.lat, d.lng]} icon={pinIcon}>
                  <Popup>
                    <div style={{ fontFamily: "Manrope, sans-serif" }}>
                      <strong>{d.name}</strong>
                      <br />
                      {d.address}
                      <br />
                      {d.phone}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
